import React from "react";
import styled from "styled-components";

// ============================================================
// Sample data used by all style variants
// ============================================================
const defaultPublications = [
  {
    authors: "Thirumalai A, Page ST",
    title: "Recent developments in male contraception",
    journal: "Drugs",
    year: 2019,
    detail: "79(1):11-20",
  },
  {
    authors: "Yuen F, et al.",
    title:
      "Combined nestorone\u2013testosterone gel suppresses serum gonadotropins to concentrations associated with effective contraception in men",
    journal: "J Clin Endocrinol Metab",
    year: 2020,
    detail: "105(4):e1230-e1240",
  },
  {
    authors: "Long JE, et al.",
    title: "Hormonal male contraception: progress and challenges",
    journal: "Best Pract Res Clin Obstet Gynaecol",
    year: 2023,
    detail: "90:102386",
  },
  {
    authors: "Population Council",
    title: "Phase 2b clinical trial results for NES/T combination gel",
    journal: "Clinical Study Report",
    year: 2024,
    detail: "",
  },
  {
    authors: "Contraline, Inc.",
    title: "NES/T Gel licensing and Phase 3 development plans",
    journal: "Press Release",
    year: 2025,
    detail: "",
  },
];

// ============================================================
// STYLE A — "Scientific Table"
// Structured rows with year column, horizontal rules, monospace
// accents. Feels like a reference database or PubMed result set.
// ============================================================

const TableWrapper = styled.div`
  max-width: 760px;
  margin: 0 auto;
  border-top: 2px solid ${(p) => p.theme.colors.dark};
`;

const TableRow = styled.div`
  display: flex;
  align-items: baseline;
  padding: 1.25rem 0;
  border-bottom: 1px solid ${(p) => p.theme.colors.greyAccent};
  gap: 1.5rem;

  ${(p) => p.theme.media.mobile`
    flex-direction: column;
    gap: 0.25rem;
  `};
`;

const TableYear = styled.span`
  font-family: "Courier New", Courier, monospace;
  font-size: 0.85rem;
  color: ${(p) => p.theme.colors.greyDark};
  min-width: 3rem;
  flex-shrink: 0;
`;

const TableBody = styled.div`
  font-family: "Avenir Next", sans-serif;
  font-size: 0.95rem;
  line-height: 1.55;
  color: ${(p) => p.theme.colors.dark};
`;

const TableJournal = styled.span`
  font-style: italic;
  color: ${(p) => p.theme.colors.greyTimeline};
`;

export const ScientificTable = ({ publications }) => {
  const pubs = publications || defaultPublications;
  return (
    <TableWrapper>
      {pubs.map((pub, i) => (
        <TableRow key={i}>
          <TableYear>{pub.year}</TableYear>
          <TableBody>
            {pub.authors}. &ldquo;{pub.title}.&rdquo;{" "}
            <TableJournal>
              {pub.journal}
              {pub.detail ? `. ${pub.detail}` : ""}
            </TableJournal>
            .
          </TableBody>
        </TableRow>
      ))}
    </TableWrapper>
  );
};

// ============================================================
// STYLE B — "Editorial"
// Serif type, generous whitespace, large pull-quote style title,
// subtle numbering. Inspired by NEJM / Nature editorial pages.
// ============================================================

const EditorialList = styled.div`
  max-width: 680px;
  width: 100%;
  margin: 0 auto;
  padding: 0;
  border-top: 1px solid #eaeaea;
  border-bottom: 1px solid #eaeaea;
  overflow: hidden;
`;

const EditorialRow = styled.div`
  display: flex;
  align-items: baseline;
  padding: 1.5rem 0;
  border-bottom: 1px solid #eaeaea;
  gap: 1.5rem;

  &:last-child {
    border-bottom: none;
  }

  ${(p) => p.theme.media.mobile`
    flex-direction: column;
    gap: 0.25rem;
  `};
`;

const EditorialYear = styled.span`
  font-family: "Courier New", Courier, monospace;
  font-size: 0.85rem;
  color: ${(p) => p.theme.colors.greyDark};
  min-width: 3rem;
  flex-shrink: 0;
`;

const EditorialBody = styled.div`
  flex: 1;
  min-width: 0;
  overflow-wrap: break-word;
  word-break: break-word;
`;

const EditorialTitle = styled.div`
  font-family: Palatino, "Palatino Linotype", Georgia, serif;
  font-size: 1.05rem;
  font-weight: bold;
  line-height: 1.5;
  color: ${(p) => p.theme.colors.dark};
  margin-bottom: 0.35rem;
`;

const EditorialMeta = styled.div`
  font-family: "Avenir Next", sans-serif;
  font-size: 0.8rem;
  color: ${(p) => p.theme.colors.greyDark};
  letter-spacing: 0.02em;

  ${(p) => p.theme.media.mobile`
    font-size: 0.95rem;
  `};
`;

const DoiLink = styled.a`
  font-family: "Avenir Next", sans-serif;
  font-size: 0.8rem;
  color: ${(p) => p.theme.colors.greyDark};
  text-decoration: underline;
  display: inline-block;
  margin-top: 0.25rem;
  overflow-wrap: break-word;
  word-break: break-all;
  max-width: 100%;

  &:hover {
    color: ${(p) => p.theme.colors.dark};
  }

  ${(p) => p.theme.media.mobile`
    font-size: 0.95rem;
  `};
`;

export const Editorial = ({ publications }) => {
  const pubs = (publications || defaultPublications).slice().sort((a, b) => (b.year || 0) - (a.year || 0));
  return (
    <EditorialList>
      {pubs.map((pub, i) => (
        <EditorialRow key={i}>
          <EditorialYear>{pub.year}</EditorialYear>
          <EditorialBody>
            <EditorialTitle>
              {pub.title}
            </EditorialTitle>
            <EditorialMeta>
              {pub.authors} &middot; <em>{pub.journal}</em>
              {pub.detail ? `, ${pub.detail}` : ""}
            </EditorialMeta>
            {pub.doi && (
              <div>
                <DoiLink href={pub.doi} target="_blank" rel="noopener noreferrer">
                  {pub.doi}
                </DoiLink>
              </div>
            )}
            {pub.link && !pub.doi && (
              <div>
                <DoiLink href={pub.link} target="_blank" rel="noopener noreferrer">
                  {pub.link}
                </DoiLink>
              </div>
            )}
          </EditorialBody>
        </EditorialRow>
      ))}
    </EditorialList>
  );
};

// ============================================================
// STYLE C — "Biotech Card"
// Teal left-border accent cards on light background. Clean sans-
// serif, year badges, high contrast. Conveys clinical trust.
// ============================================================

const CardList = styled.div`
  max-width: 720px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const PubCard = styled.div`
  border-left: 3px solid ${(p) => p.theme.colors.teal};
  background: ${(p) => p.theme.colors.white};
  padding: 1.25rem 1.5rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
  border-radius: 0 4px 4px 0;
  transition: box-shadow 200ms;

  &:hover {
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  }
`;

const CardTitle = styled.div`
  font-family: "Avenir Next", sans-serif;
  font-size: 0.95rem;
  font-weight: 600;
  line-height: 1.45;
  color: ${(p) => p.theme.colors.dark};
  margin-bottom: 0.5rem;
`;

const CardFooter = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex-wrap: wrap;
`;

const YearBadge = styled.span`
  font-family: "Avenir Next", sans-serif;
  font-size: 0.75rem;
  font-weight: 600;
  background: ${(p) => p.theme.colors.teal};
  color: white;
  padding: 0.15rem 0.5rem;
  border-radius: 3px;
`;

const CardMeta = styled.span`
  font-family: "Avenir Next", sans-serif;
  font-size: 0.8rem;
  color: ${(p) => p.theme.colors.greyDark};
`;

export const BiotechCards = ({ publications }) => {
  const pubs = publications || defaultPublications;
  return (
    <CardList>
      {pubs.map((pub, i) => (
        <PubCard key={i}>
          <CardTitle>{pub.title}</CardTitle>
          <CardFooter>
            <YearBadge>{pub.year}</YearBadge>
            <CardMeta>
              {pub.authors} &middot; <em>{pub.journal}</em>
              {pub.detail ? ` ${pub.detail}` : ""}
            </CardMeta>
          </CardFooter>
        </PubCard>
      ))}
    </CardList>
  );
};

// ============================================================
// STYLE D — "Trust Grid"
// Two-column grid with dark header bar, alternating row tones,
// and a compact data-dense layout. Institutional / FDA-doc feel.
// ============================================================

const GridOuter = styled.div`
  max-width: 760px;
  margin: 0 auto;
  border: 1px solid ${(p) => p.theme.colors.greyAccent};
  border-radius: 4px;
  overflow: hidden;
`;

const GridHeader = styled.div`
  display: flex;
  background: ${(p) => p.theme.colors.dark};
  color: ${(p) => p.theme.colors.white};
  font-family: "Avenir Next", sans-serif;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  padding: 0.6rem 1rem;

  & > span:first-child {
    flex: 1;
  }
  & > span:last-child {
    width: 160px;
    text-align: right;
  }

  ${(p) => p.theme.media.mobile`
    & > span:last-child { display: none; }
  `};
`;

const GridRow = styled.div`
  display: flex;
  align-items: baseline;
  padding: 0.85rem 1rem;
  font-family: "Avenir Next", sans-serif;
  font-size: 0.9rem;
  line-height: 1.5;
  background: ${(p) =>
    p.even ? p.theme.colors.greyLight : p.theme.colors.white};
  color: ${(p) => p.theme.colors.dark};

  & > div:first-child {
    flex: 1;
  }
  & > div:last-child {
    width: 160px;
    text-align: right;
    font-size: 0.8rem;
    color: ${(p) => p.theme.colors.greyDark};
  }

  ${(p) => p.theme.media.mobile`
    flex-direction: column;
    & > div:last-child {
      width: 100%;
      text-align: left;
      margin-top: 0.25rem;
    }
  `};
`;

const GridTitle = styled.span`
  font-weight: 600;
`;

const GridAuthors = styled.span`
  color: ${(p) => p.theme.colors.greyTimeline};
`;

export const TrustGrid = ({ publications }) => {
  const pubs = publications || defaultPublications;
  return (
    <GridOuter>
      <GridHeader>
        <span>Publication</span>
        <span>Source</span>
      </GridHeader>
      {pubs.map((pub, i) => (
        <GridRow key={i} even={i % 2 === 1}>
          <div>
            <GridTitle>{pub.title}</GridTitle>
            <br />
            <GridAuthors>{pub.authors}</GridAuthors>
          </div>
          <div>
            <em>{pub.journal}</em>
            {pub.detail ? `, ${pub.detail}` : ""} ({pub.year})
          </div>
        </GridRow>
      ))}
    </GridOuter>
  );
};



// WEBPACK FOOTER //
// ./src/components/PublicationStyles.js